import { Component, computed, DestroyRef, effect, ElementRef, inject, signal } from '@angular/core';
import { DocumentConfigPartial, DocumentItemPartial, DocumentItemTypeEnum } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaEditorComponent } from 'src/app/shared/components/ra-editor/ra-editor.component';
import { RaClickOutsideDirective } from 'src/app/shared/directives';
import {
    TEXT_COLOR_CHOICES,
    defaultTextSizePt,
    formatPt,
    parseItemTextColor,
    parseItemTextSize,
    parseSizeInput,
    stepTextSize,
} from '../../../../text-style-options';
import { DOCUMENT_FONT_FAMILY_OPTIONS } from '../../constants/document-font-select-options.generated';
import { RaEditTooltipDirective } from '../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { RailPopoverService } from '../cards/_card-base/rail-popover.service';
import { watchMenuDismiss } from '../cards/_card-base/rail-menu.directive';
import { ABNT_FONT_FAMILIES } from '../modals/modal-document-config/modal-document-config.component';
import { EditDataService } from '../../services/_edit-data.service';
import { EditItemsService } from '../../services/edit-items.service';
import { EditService } from '../../services/edit-service';
import { TextEditingService } from '../../services/text-editing.service';

type TextKind = 'text' | 'callout' | 'media' | 'table' | 'none';
type TextAlign = 'left' | 'justify';

/** Opção do seletor de tipo do texto. */
interface TextTypeOption {
    type: DocumentItemTypeEnum;
    icon: string;
    labelKey: string;
}

/**
 * Barra de formatação fixa no topo do editor (segunda linha do cabeçalho), no lugar do
 * chip de tipo e do chip "A" que moravam no rail de cada card e do composer de rodapé.
 * Ver docs/PLANO-EDICAO-INLINE.md.
 *
 * Age no campo ATIVO (`TextEditingService.activeFieldId`, o último tocado na folha):
 * - tipo (título, subtítulo, texto) e tamanho/cor do campo são configs do item, como
 *   sempre foram (`changeFieldType`, `textSize`, `textColor`);
 * - negrito, itálico, sublinhado e a cor NA SELEÇÃO vão pelo Quill do editor em foco;
 * - fonte e alinhamento são do documento (`config.fontFamily` e `config.textAlign`);
 * - "Inserir campo" abre o menu de campos ancorado aqui (é a âncora padrão do menu).
 *
 * Os botões não roubam o foco do editor (pointerdown com preventDefault): senão o negrito
 * perderia o trecho selecionado antes de ser aplicado.
 */
@Component({
    selector: 'edit-toolbar',
    templateUrl: 'edit-toolbar.component.html',
    styleUrl: 'edit-toolbar.component.scss',
    imports: [RaIconComponent, RaClickOutsideDirective, RaEditTooltipDirective, TranslatePipe],
})
export class EditToolbarComponent {
    public readonly colorChoices = TEXT_COLOR_CHOICES;
    /** Marca-texto: fundos claros, para o texto continuar legível impresso. */
    public readonly highlightChoices = ['#fff3a3', '#ffe7ce', '#d0f2e4', '#d4e7fc', '#ece6fb', '#fde2e2'];
    /** As mesmas famílias do modal de configurações; o valor gravado é o nome puro. */
    public readonly fontOptions = DOCUMENT_FONT_FAMILY_OPTIONS.map(family => ({
        family,
        label: ABNT_FONT_FAMILIES.includes(family) ? `${prettyFont(family)} (ABNT)` : prettyFont(family),
    }));
    public readonly typeOptions: TextTypeOption[] = [
        { type: DocumentItemTypeEnum.TITLE, icon: 'text', labelKey: 'document.edit.textCard.typeTitle' },
        { type: DocumentItemTypeEnum.SUBTITLE, icon: 'smallcaps', labelKey: 'document.edit.textCard.typeSubtitle' },
        { type: DocumentItemTypeEnum.TEXT, icon: 'textalign-justifyleft', labelKey: 'document.edit.textCard.typeText' },
    ];

    private static readonly SAVE_DEBOUNCE_MS = 400;
    private static readonly TYPE_DEBOUNCE_MS = 1000;

    public editDataService = inject(EditDataService);
    private _textEditing = inject(TextEditingService);
    private _editItems = inject(EditItemsService);
    private _editService = inject(EditService);
    private _translate = inject(TranslateService);
    private _railPopovers = inject(RailPopoverService);
    private _element = inject<ElementRef<HTMLElement>>(ElementRef);

    /** Campo alvo da barra: o último tocado, se ainda estiver na seção aberta. */
    public activeField = computed<DocumentItemPartial | null>(() => {
        const id = this._textEditing.activeFieldId();
        if (!id) return null;
        return this.editDataService.currentSession()?.items?.find(item => item._id === id) ?? null;
    });

    public kind = computed<TextKind>(() => {
        switch (this.activeField()?.type) {
            case DocumentItemTypeEnum.TITLE:
            case DocumentItemTypeEnum.SUBTITLE:
            case DocumentItemTypeEnum.TEXT:
                return 'text';
            case DocumentItemTypeEnum.CALLOUT:
                return 'callout';
            case DocumentItemTypeEnum.IMAGE:
            case DocumentItemTypeEnum.GROUP_IMAGE:
            case DocumentItemTypeEnum.VIDEO:
            case DocumentItemTypeEnum.GROUP_VIDEO:
                return 'media';
            case DocumentItemTypeEnum.TABLE:
                return 'table';
            default:
                return 'none';
        }
    });

    /**
     * Sem o `loading` do editor de propósito: cada save de texto liga o loading por um
     * instante, e a barra apagando e acendendo a cada pausa na digitação cansava a vista.
     */
    public blocked = computed(
        () =>
            this.editDataService.currentSessionIsDisabled() ||
            this.editDataService.documentIsBlocked()
    );

    public canType = computed(() => this.kind() === 'text' && !this.blocked());
    /**
     * O que a barra acende: num item de texto em foco, a classe da LINHA do cursor (formato
     * `header` do Delta, ver setType); senão, o tipo do item.
     */
    public currentType = computed<DocumentItemTypeEnum | null>(() => {
        const field = this.activeField();
        if (!field) return null;
        if (field.type === DocumentItemTypeEnum.TEXT && this.editorFocused()) {
            const header = this._textEditing.selectionFormat()['header'];
            if (header === 1) return DocumentItemTypeEnum.TITLE;
            if (header === 2) return DocumentItemTypeEnum.SUBTITLE;
        }
        return field.type;
    });
    public canSize = computed(() => (this.kind() !== 'none' || this.hasSelection()) && !this.blocked());
    /** Cor do campo: destaque e tabela têm a família de cor deles. */
    public canColor = computed(
        () => (this.kind() === 'text' || this.kind() === 'media') && !this.blocked()
    );
    /** Editor em foco: negrito, itálico, sublinhado e cor na seleção. */
    public editorFocused = computed(() => !!this._textEditing.editingFieldId() && !this.blocked());
    /** Trecho selecionado no editor em foco: tamanho, fonte e cor agem no trecho, não no campo. */
    public hasSelection = computed(() => this.editorFocused() && this._textEditing.hasSelection());

    /** Tamanho (pt) e fonte do trecho selecionado, quando uniformes; null = sem formato próprio. */
    private _selectionSize = computed<number | null>(() => {
        const raw = this._textEditing.selectionFormat()['size'];
        const pt = typeof raw === 'string' ? Number(raw) : NaN;
        return Number.isFinite(pt) ? pt : null;
    });
    private _selectionFont = computed<string | null>(() => {
        const raw = this._textEditing.selectionFormat()['font'];
        return typeof raw === 'string' ? raw : null;
    });

    public isBold = computed(() => !!this._textEditing.selectionFormat()['bold']);
    public isItalic = computed(() => !!this._textEditing.selectionFormat()['italic']);
    public isUnderline = computed(() => !!this._textEditing.selectionFormat()['underline']);

    // ── Tamanho (a mesma regra do chip "A" que existia no rail) ──────────────
    public defaultSize = computed(() =>
        defaultTextSizePt(this.activeField()?.type, this.editDataService.document()?.config?.fontSize)
    );
    public shownSize = computed<number | null>(() => {
        // Trecho selecionado com tamanho próprio: é ele que a barra mostra e mexe.
        if (this.hasSelection() && this._selectionSize() !== null) return this._selectionSize();

        const field = this.activeField();
        const live = this._textEditing.liveStyle();
        if (field && live?.fieldId === field._id && live.textSize !== undefined) return live.textSize;
        return parseItemTextSize(field?.configs?.textSize);
    });
    public sizeValue = computed(() => this.shownSize() ?? this.defaultSize());
    public sizeDraft = signal<string | null>(null);
    public sizeText = computed(
        () => this.sizeDraft() ?? formatPt(this.sizeValue(), this._translate.getCurrentLang())
    );

    // ── Cor do campo ─────────────────────────────────────────────────────────
    /** A cor mostrada: a do trecho sob o cursor no editor em foco, se ele tiver uma; senão a do campo. */
    public shownColor = computed<string | null>(() => {
        if (this.editorFocused()) {
            const selection = this._textEditing.selectionFormat()['color'];
            const spanColor = typeof selection === 'string' ? parseItemTextColor(selection) : null;
            if (spanColor !== null) return spanColor;
        }

        const field = this.activeField();
        const live = this._textEditing.liveStyle();
        if (field && live?.fieldId === field._id && live.textColor !== undefined) return live.textColor;
        return parseItemTextColor(field?.configs?.textColor);
    });
    public isCustomColor = computed(() => {
        const color = this.shownColor();
        return color !== null && !TEXT_COLOR_CHOICES.some(choice => choice.hex === color);
    });
    public showColorMenu = signal(false);
    public showFontMenu = signal(false);
    public showHighlightMenu = signal(false);

    /** Fundo (marca-texto) e lista do trecho/linha em foco, para a barra acender. */
    public currentHighlight = computed<string | null>(() => {
        const raw = this._textEditing.selectionFormat()['background'];
        return typeof raw === 'string' ? raw : null;
    });
    public currentList = computed<string | null>(() => {
        const raw = this._textEditing.selectionFormat()['list'];
        return typeof raw === 'string' ? raw : null;
    });

    // ── Documento ────────────────────────────────────────────────────────────
    public textAlign = computed<TextAlign>(() =>
        this.editDataService.document()?.config?.textAlign === 'left' ? 'left' : 'justify'
    );
    public currentFont = computed(() => this.editDataService.documentFontFamily());
    /** A fonte mostrada: a do trecho selecionado, se ele tiver uma; senão a do documento. */
    public shownFont = computed(() =>
        this.hasSelection() ? (this._selectionFont() ?? this.currentFont()) : this.currentFont()
    );
    public fontLabel = computed(() => prettyFont(this.shownFont()));

    private _saveTimer: ReturnType<typeof setTimeout> | null = null;
    private _typeTimer: ReturnType<typeof setTimeout> | null = null;
    private _savedType: DocumentItemTypeEnum | null = null;

    /** Algum dropdown da barra (fonte, cor, marca-texto) aberto. */
    private _menuOpen = computed(
        () => this.showFontMenu() || this.showColorMenu() || this.showHighlightMenu()
    );

    constructor() {
        inject(DestroyRef).onDestroy(() => this._flushStyle());

        /*
         * Os dropdowns da barra entram na fila dos menus do rail: o rail dos cards para a
         * propagação do clique, e o clickOutside daqui não via o ⋯ de um card abrindo, então
         * os dois ficavam abertos juntos. Abrir um fecha o outro. Esc e a rolagem da folha
         * também fecham, como nos menus do rail.
         */
        effect(onCleanup => {
            if (!this._menuOpen()) return;
            const close = (): void => this.closeMenus();
            const release = this._railPopovers.open(close);
            const unwatch = watchMenuDismiss(this._element.nativeElement, close);
            onCleanup(() => {
                release();
                unwatch();
            });
        });
    }

    public closeMenus(): void {
        this.showFontMenu.set(false);
        this.showColorMenu.set(false);
        this.showHighlightMenu.set(false);
    }

    /** Botões não roubam a seleção do editor (senão o negrito perde o trecho). */
    public keepSelection(event: Event): void {
        event.preventDefault();
    }

    // ── Tipo ─────────────────────────────────────────────────────────────────

    /**
     * Título, subtítulo e texto são a CLASSE DE UMA LINHA, no mesmo campo: num item de texto
     * o botão marca a linha do cursor (ou as selecionadas) com o formato `header` do Delta
     * (1 = título, 2 = subtítulo, nada = texto), e o PDF e o sumário leem essas linhas como
     * leem os itens TITLE/SUBTITLE. O item continua TEXT; nada de campo novo.
     *
     * Itens TITLE/SUBTITLE (da IA, do app, de antes) continuam trocando de tipo inteiros,
     * como sempre. A exceção é o de várias linhas com só parte delas mudando de classe: vira
     * um texto em que cada linha carrega a própria classe, e a conversão sai na hora para a
     * escrita do tipo chegar antes do save do texto.
     */
    public setType(newType: DocumentItemTypeEnum): void {
        const field = this.activeField();
        if (!field || !this.canType()) return;

        const editor = this._textEditing.activeEditor();

        if (field.type === DocumentItemTypeEnum.TEXT) {
            if (editor) {
                editor.format('header', headerOf(newType));
                if (newType === DocumentItemTypeEnum.TEXT) this._applyDocumentDefaults(field, editor);
                return;
            }
            // Sem editor por perto (campo recém-criado): vale para o campo inteiro, como antes.
        } else if (newType !== field.type && editor && editor.lineCount() > 1) {
            const selected = editor.selectedLineCount();
            if (selected > 0 && selected < editor.lineCount()) {
                editor.formatAllLines('header', headerOf(field.type));
                editor.format('header', headerOf(newType));
                this._convertType(field, DocumentItemTypeEnum.TEXT, true);
                return;
            }
        }

        if (field.type !== newType) this._convertType(field, newType, false);
    }

    /**
     * "Texto" é o layout padrão do documento (pedido do usuário): as linhas voltam à fonte e
     * ao tamanho da config do documento (sem `size`/`font` por trecho) e o tamanho próprio do
     * campo sai, para o corpo seguir o "Tamanho do texto" das configurações. Cor, negrito e
     * marca-texto ficam: são escolhas do trecho, não do layout.
     */
    private _applyDocumentDefaults(field: DocumentItemPartial, editor: RaEditorComponent): void {
        editor.formatSelectedLinesText({ size: false, font: false });
        if (parseItemTextSize(field.configs?.textSize) !== null) this._setLive({ textSize: null }, true);
    }

    /**
     * Converte o item entre TEXT, TITLE e SUBTITLE: mudança visual imediata (otimista) e
     * save com debounce de 1s, para agrupar cliques rápidos entre os três (`now` salva já).
     */
    private _convertType(field: DocumentItemPartial, newType: DocumentItemTypeEnum, now: boolean): void {
        this._savedType ??= field.type;
        field.type = newType;

        if (this._typeTimer) clearTimeout(this._typeTimer);
        this._typeTimer = null;

        // Voltou ao tipo já salvo: nada a persistir.
        if (this._savedType === newType) {
            this._savedType = null;
            return;
        }

        const save = (): void => {
            const revertType = this._savedType;
            this._savedType = null;
            this._editItems.changeFieldType(field._id, newType, () => {
                if (revertType != null) field.type = revertType;
            });
        };

        if (now) save();
        else this._typeTimer = setTimeout(save, EditToolbarComponent.TYPE_DEBOUNCE_MS);
    }

    // ── Tamanho ──────────────────────────────────────────────────────────────

    public stepSize(direction: 1 | -1): void {
        if (!this.canSize()) return;
        this.sizeDraft.set(null);

        const next = stepTextSize(this.sizeValue(), direction);
        // Trecho selecionado: o tamanho é do trecho (formato `size` do Delta, em pt).
        const editor = this._selectionEditor();
        if (editor) {
            editor.format('size', String(next));
            return;
        }

        this._setLive({ textSize: next });
    }

    public onSizeInput(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.sizeDraft.set(raw);
        const size = parseSizeInput(raw);
        if (size !== null) this._setLive({ textSize: size });
    }

    /** Enter ou saída do campo: salva já e mostra o número formatado. */
    public commitSize(): void {
        this.sizeDraft.set(null);
        this._flushStyle();
    }

    public onSizeKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            this.stepSize(event.key === 'ArrowUp' ? 1 : -1);
        }
    }

    /** "Padrão": volta ao tamanho do tipo na hora (ou tira o tamanho próprio do trecho). */
    public useDefaultSize(): void {
        this.sizeDraft.set(null);

        const editor = this._selectionEditor();
        if (editor) {
            editor.format('size', false);
            return;
        }

        this._setLive({ textSize: null }, true);
    }

    /** O editor em foco, quando a seleção cobre um trecho (senão null: vale o campo). */
    private _selectionEditor(): RaEditorComponent | null {
        const editor = this._textEditing.editor();
        return editor && this.editorFocused() && editor.hasSelection() ? editor : null;
    }

    // ── Negrito, itálico, sublinhado ─────────────────────────────────────────

    public toggleFormat(name: 'bold' | 'italic' | 'underline'): void {
        const editor = this._textEditing.editor();
        if (!editor || !this.editorFocused()) return;

        const current = !!this._textEditing.selectionFormat()[name];
        editor.format(name, !current);
    }

    // ── Marca-texto, listas e limpar ─────────────────────────────────────────

    /** Fundo do trecho (formato `background` do Delta); `null` tira. Sem trecho, vale para o que vier. */
    public selectHighlight(hex: string | null): void {
        this.showHighlightMenu.set(false);
        const editor = this._textEditing.editor();
        if (!editor || !this.editorFocused()) return;
        editor.format('background', hex ?? false);
    }

    /** Lista com marcadores ou numerada na linha do cursor (formato de bloco do Quill). */
    public toggleList(type: 'bullet' | 'ordered'): void {
        const editor = this._textEditing.editor();
        if (!editor || !this.editorFocused()) return;
        editor.format('list', this.currentList() === type ? false : type);
    }

    public clearFormat(): void {
        const editor = this._textEditing.editor();
        if (!editor || !this.editorFocused()) return;
        editor.clearFormat();
    }

    // ── Cor ──────────────────────────────────────────────────────────────────

    /**
     * Com um trecho selecionado no editor, a cor é do trecho (Delta, como o pincel do
     * editor antigo); sem seleção, é a cor do campo inteiro (config `textColor`).
     * `null` = padrão.
     */
    public selectColor(hex: string | null): void {
        const color = hex === null ? null : parseItemTextColor(hex);
        if (hex !== null && color === null) return;

        const editor = this._selectionEditor();
        if (editor) {
            editor.format('color', color ?? false);
            this.showColorMenu.set(false);
            return;
        }

        if (!this.canColor()) return;
        this._setLive({ textColor: color }, true);
        this.showColorMenu.set(false);
    }

    /** Arrastando no seletor livre: o card muda na hora; o save espera o usuário parar. */
    public onColorInput(event: Event): void {
        const color = parseItemTextColor((event.target as HTMLInputElement).value);
        if (color === null) return;

        const editor = this._selectionEditor();
        if (editor) {
            editor.format('color', color);
            return;
        }

        if (this.canColor()) this._setLive({ textColor: color });
    }

    /** O seletor livre fechou: salva já. */
    public onColorChange(event: Event): void {
        this.onColorInput(event);
        this._flushStyle();
    }

    // ── Alinhamento do documento ─────────────────────────────────────────────

    public setAlign(align: TextAlign): void {
        if (this.blocked() || this.textAlign() === align) return;
        this._saveDocumentConfig({ textAlign: align });
    }

    /**
     * Com um trecho selecionado, a fonte é do trecho (formato `font` do Delta); sem seleção,
     * é a do documento inteiro (config `fontFamily`).
     */
    public selectFont(family: string): void {
        this.showFontMenu.set(false);
        if (this.blocked()) return;

        const editor = this._selectionEditor();
        if (editor) {
            editor.format('font', family === this.currentFont() ? false : family);
            return;
        }

        if (family === this.currentFont()) return;

        // Sem seleção é "tudo na fonte nova": os trechos com fonte própria também.
        this._saveDocumentConfig({ fontFamily: family }, () => this._editItems.clearSpanFonts());
    }

    /**
     * Grava uma config do documento como o modal de definições faz: aplica na hora (o botão
     * acende e a folha muda no clique, não depois da resposta), liga o "Salvando" e desfaz
     * se a API recusar.
     */
    private _saveDocumentConfig(patch: Partial<DocumentConfigPartial>, onSaved?: () => void): void {
        const previous = this.editDataService.document()?.config;
        const config = { ...(previous ?? {}), ...patch } as DocumentConfigPartial;
        this.editDataService.applyDocumentConfig(config);

        this.editDataService.beginSave();
        this._editService.saveDocumentConfig(config, false).subscribe({
            next: () => onSaved?.(),
            complete: () => this.editDataService.endSave(),
            error: error => {
                console.error(error);
                this.editDataService.applyDocumentConfig(previous as DocumentConfigPartial);
                this.editDataService.endSave(false);
            },
        });
    }

    // ── Estilo ao vivo + save com debounce ───────────────────────────────────

    private _setLive(patch: { textSize?: number | null; textColor?: string | null }, now = false): void {
        const field = this.activeField();
        if (!field) return;

        const current = this._textEditing.liveStyle();
        const base = current?.fieldId === field._id ? current : { fieldId: field._id };
        this._textEditing.liveStyle.set({ ...base, ...patch });

        if (this._saveTimer) clearTimeout(this._saveTimer);
        if (now) this._flushStyle();
        else this._saveTimer = setTimeout(() => this._flushStyle(), EditToolbarComponent.SAVE_DEBOUNCE_MS);
    }

    /**
     * Grava o que mudou de verdade (o chip do rail fazia igual): `null` apaga a chave na
     * API, que mescla. O card volta a ler a config salva, já aplicada de forma otimista.
     */
    private _flushStyle(): void {
        if (this._saveTimer) clearTimeout(this._saveTimer);
        this._saveTimer = null;

        const live = this._textEditing.liveStyle();
        if (!live) return;
        this._textEditing.liveStyle.set(null);

        const field = this.editDataService
            .currentSession()
            ?.items?.find(item => item._id === live.fieldId);
        if (!field) return;

        const patch: Record<string, number | string | null> = {};
        if (live.textSize !== undefined && live.textSize !== parseItemTextSize(field.configs?.textSize))
            patch['textSize'] = live.textSize;
        if (live.textColor !== undefined && live.textColor !== parseItemTextColor(field.configs?.textColor))
            patch['textColor'] = live.textColor;
        if (Object.keys(patch).length === 0) return;

        this._editItems.updateItemConfig(field._id, { ...(field.configs ?? {}), ...patch });
    }
}

/** Formato `header` do Delta que corresponde à classe: título 1, subtítulo 2, texto nenhum. */
function headerOf(type: DocumentItemTypeEnum): 1 | 2 | false {
    if (type === DocumentItemTypeEnum.TITLE) return 1;
    if (type === DocumentItemTypeEnum.SUBTITLE) return 2;
    return false;
}

/** "CenturyGothic" lê como "Century Gothic"; os outros nomes já vêm com espaço. */
function prettyFont(family: string): string {
    return family.replace(/([a-z])([A-Z])/g, '$1 $2');
}
